import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Pencil, Trash2, Gavel, UserCheck, CalendarX2, HeartHandshake, CheckCircle2 } from "lucide-react";
import { ConflictStatusChip } from "@/components/ConflictDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";
import DayPreviewSheet from "@/components/DayPreviewSheet";
import { TapArrow, TapInfo } from "@/components/TapRow";
import { CHIP } from "@/components/MemberAttendanceCard";
import { useScrollPaging } from "@/lib/useScrollPaging";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Weekday pills in calendar order (Sun-first). Values use the backend convention: Mon=0 … Sun=6.
export const WEEKDAY_PILLS = [6, 0, 1, 2, 3, 4, 5].map((v) => ({ value: v, label: DOW[(v + 1) % 7] }));
export const repeatDaysLabel = (days) => WEEKDAY_PILLS.filter((p) => (days || []).includes(p.value)).map((p) => p.label).join(", ");
// Badge text for a repeating event: "weekly" / "monthly" / "yearly", or the chosen days for custom.
export const recurrenceLabel = (it) => (it.recurrence === "custom" ? repeatDaysLabel(it.repeat_days) : it.recurrence);

function ymd(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function prettyDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

// Outline pill action; `compact` shows only the icon on phones (label returns from sm up).
function PillButton({ icon: Icon, label, onClick, testId, className = "", compact = false }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick} data-testid={testId} aria-label={label} title={label}
      className={`h-7 rounded-full ${compact ? "w-7 px-0 sm:w-auto sm:px-3" : ""} ${className}`}>
      <Icon className={`h-3.5 w-3.5 ${compact ? "sm:mr-1" : "mr-1"}`} /><span className={compact ? "hidden sm:inline" : ""}>{label}</span>
    </Button>
  );
}

// conflictInfo(it) -> null | { conflict, canSubmit } (member view only); onConflict(it) opens the request form.
function ConflictControls({ it, info, onConflict, prefix = "", compact = false }) {
  if (!info || it.type !== "event") return null;
  return (
    <>
      {info.conflict && <ConflictStatusChip status={info.conflict.status} testId={`${prefix}conflict-chip-${it.id}`} />}
      {info.canSubmit && <PillButton icon={CalendarX2} label="Conflict" className="text-orange-600" compact={compact} onClick={() => onConflict(it)} testId={`${prefix}conflict-btn-${it.id}`} />}
    </>
  );
}

function AttendanceButton({ onClick, testId, compact = false }) {
  return <PillButton icon={UserCheck} label="Attendance" compact={compact} onClick={onClick} testId={testId} />;
}

// Members: one "Signed up" chip per block they're on; the Volunteer button only shows while another open block remains.
function VolunteerControls({ it, onVolunteer, prefix = "", compact = false }) {
  if (it.type !== "volunteer") return null;
  const mine = it.my_shifts || [];
  const canSign = it.can_sign_up !== false;
  return (
    <>
      {mine.map((m) => (
        <span key={m.block_id} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 tabular-nums" data-testid={`${prefix}volunteer-signed-${m.block_id}`}>
          <CheckCircle2 className="h-3 w-3" /> Signed up · {fmtTime(m.start_time)}–{fmtTime(m.end_time)}{m.given_up ? " (given up — waiting for coach)" : ""}
        </span>
      ))}
      <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 tabular-nums" data-testid={`${prefix}volunteer-fill-${it.id}`}>{it.filled} of {it.spots} filled</span>
      {onVolunteer && canSign && <PillButton icon={HeartHandshake} label="Volunteer" className="text-indigo-700" compact={compact} onClick={() => onVolunteer(it)} testId={`${prefix}volunteer-open-${it.id}`} />}
    </>
  );
}

const MONTH_PAGE = 5;
// Member conflict tint per status (opaque so the pinned date column matches the row).
const CONFLICT_TINT = {
  pending: { bg: "bg-amber-50", bar: "border-l-amber-400", rowBar: "!border-l-amber-400", label: "Pending" },
  approved: { bg: "bg-emerald-50", bar: "border-l-emerald-500", rowBar: "!border-l-emerald-500", label: "Approved" },
  declined: { bg: "bg-red-50", bar: "border-l-red-400", rowBar: "!border-l-red-400", label: "Declined" },
};

function ConflictKey() {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500" data-testid="month-conflict-key">
      <span className="font-semibold text-slate-600">Conflict:</span>
      {Object.entries(CONFLICT_TINT).map(([k, t]) => (
        <span key={k} className="flex items-center gap-1.5"><span className={`h-3 w-5 rounded-[3px] border-l-[3px] ${t.bar} ${t.bg}`} /> {t.label}</span>
      ))}
    </div>
  );
}

const PILL = "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide";

// One stacked event card in the monthly list. `statuses` = the member's non-present attendance marks for it.
function EventCard({ it, tint, hl, statuses = [], actions }) {
  const fade = it._past ? "opacity-60" : "";
  return (
    <div className={`rounded-xl border border-slate-200 p-4 transition-colors duration-500 ${tint ? `border-l-4 ${tint.rowBar} ${tint.bg}` : hl ? "bg-amber-50" : "bg-white"} ${hl && tint ? "ring-2 ring-amber-300" : ""}`}
      data-testid={`month-item-${it.id}`} data-highlighted={hl ? "true" : undefined} data-conflict={tint ? it._conflictStatus : undefined}>
      <div className="flex items-start justify-between gap-2">
        <div className={`shrink-0 ${fade}`} data-testid={`month-item-date-${it.id}`}>
          <p className="text-sm font-bold text-slate-800 tabular-nums">{prettyDate(it._start)}
            {it._multi && <span className="font-semibold text-slate-500" data-testid={`month-range-end-${it.id}`}> → {prettyDate(it._end)}</span>}</p>
          {it.all_day ? <p className="text-xs text-slate-500">All day</p> : it.start_time && <p className="text-xs text-slate-500 tabular-nums">{fmtTime(it.start_time)}{it.end_time ? ` – ${fmtTime(it.end_time)}` : ""}</p>}
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5 empty:hidden" data-testid={`month-title-actions-${it.id}`}>
          {statuses.map((s) => <span key={s} className={`${PILL} ${CHIP[s]}`} data-testid={`month-attendance-badge-${it.id}-${s}`}>{s}</span>)}
          {it._multi && <span className={`${PILL} bg-slate-100 text-slate-600 ${fade}`}>Multi-day</span>}
          {it._past && <span className={`${PILL} bg-slate-200 text-slate-600`} data-testid={`month-past-badge-${it.id}`}>Past</span>}
          {actions}
        </div>
      </div>
      <div className={`mt-2 ${fade}`}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`month-title-row-${it.id}`}>
          {it.type === "conference" && <Gavel className="h-3.5 w-3.5 shrink-0 text-blue-500" />}
          {it.type === "volunteer" && <HeartHandshake className="h-3.5 w-3.5 shrink-0 text-indigo-600" />}
          <p className="font-semibold leading-6 text-slate-800">{it.title}</p>
          {it.location && <p className="flex items-center gap-1 text-xs text-slate-500" data-testid={`month-location-${it.id}`}><MapPin className="h-3 w-3 shrink-0" /> {it.location}</p>}
        </div>
        {it.description && <p className="mt-1 text-sm text-slate-600">{it.description}</p>}
      </div>
    </div>
  );
}

export function ScheduleCalendar({ items, canManage = false, onEdit, onDelete, onSelectDate, showAttendance = false, onAttendance, conflictInfo, onConflict, onVolunteer, attendanceFor }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(ymd(today));
  const [monthPage, setMonthPage] = useState(1);
  const [preview, setPreview] = useState(null);
  const [flash, setFlash] = useState(null);

  useEffect(() => { if (onSelectDate) onSelectDate(selected); }, [selected, onSelectDate]);

  const byDate = useMemo(() => {
    const map = {};
    (items || []).forEach((it) => {
      const key = (it.event_date || "").slice(0, 10);
      if (!key) return;
      (map[key] = map[key] || []).push(it);
    });
    return map;
  }, [items]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayKey = ymd(today);

  // Tapping a day with events opens the preview (bottom sheet on phones, centered popup on desktop).
  const pickDay = (key) => {
    setSelected(key);
    if ((byDate[key] || []).length) setPreview(key);
  };
  // Sheet actions: close the sheet first so the Conflict / Volunteer dialog doesn't fight its focus trap.
  const fromSheet = (fn) => fn && ((it) => { setPreview(null); setTimeout(() => fn(it), 250); });
  const tintOf = (it) => CONFLICT_TINT[it.type === "event" && conflictInfo?.(it)?.conflict?.status];
  const sheetTint = (it) => { const t = tintOf(it); return t && `border-l-4 ${t.bar} ${t.bg}`; };
  const sheetActions = (it) => (
    <>
      <ConflictControls it={it} info={conflictInfo?.(it)} onConflict={fromSheet(onConflict)} prefix="preview-" />
      <VolunteerControls it={it} onVolunteer={fromSheet(onVolunteer)} prefix="preview-" />
      {showAttendance && it.type === "event" && <AttendanceButton onClick={() => fromSheet(onAttendance)(it)} testId={`preview-attendance-btn-${it.id}`} />}
    </>
  );
  // Jump to the day's rows in the monthly list (switching to the right page) and briefly highlight them.
  const viewDayDetails = () => {
    const day = preview;
    setPreview(null);
    const onDay = (it) => it._start <= day && it._end >= day;
    const pi = monthChunks.findIndex((c) => c.some(onDay));
    if (pi < 0) return;
    const also = monthChunks.map((c, i) => (i !== pi && c.some(onDay) ? i + 1 : null)).filter(Boolean);
    if (also.length) toast.info(`More events on ${prettyDate(day)} continue on page ${also.join(", ")}.`);
    setMonthPage(pi + 1);
    setFlash(day);
    setTimeout(() => document.querySelector(`[data-testid="month-item-${monthChunks[pi].find(onDay).id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    setTimeout(() => setFlash(null), 2600);
  };

  const dragStartX = useRef(null);
  const onPointerDown = (e) => { dragStartX.current = e.clientX; };
  const onPointerUp = (e) => {
    if (dragStartX.current == null) return;
    const dx = e.clientX - dragStartX.current;
    dragStartX.current = null;
    if (Math.abs(dx) < 50) return;
    setCursor(new Date(year, month + (dx < 0 ? 1 : -1), 1));
  };

  const monthPrefix = `${year}-${String(month + 1).padStart(2, "0")}`;
  // Series metadata (for whole-series delete of recurring / multi-day events).
  const seriesInfo = useMemo(() => {
    const m = {};
    (items || []).forEach((it) => {
      if (it.type === "event" && it.series_id) {
        const s = (m[it.series_id] = m[it.series_id] || { ids: [], recurrence: null, count: 0 });
        s.ids.push(it.id);
        s.count += 1;
        if (it.recurrence) s.recurrence = recurrenceLabel(it);
      }
    });
    return m;
  }, [items]);

  // Decorate an item for deletion: recurring/multi-day series delete removes every occurrence.
  const forDelete = (it) => {
    const s = it.series_id && seriesInfo[it.series_id];
    if (!s) return { ...it, _ids: [it.id] };
    if (s.recurrence) return { ...it, _ids: s.ids, _recurring: true, _recurrence: s.recurrence, _seriesCount: s.count };
    return { ...it, _ids: s.ids, _multi: s.count > 1 };
  };

  // Collapse contiguous multi-day series into one spanning row; recurring series show one row per occurrence.
  const monthItems = useMemo(() => {
    const seriesMap = {};
    const singles = [];
    (items || []).forEach((it) => {
      if (it.type === "event" && it.series_id) {
        (seriesMap[it.series_id] = seriesMap[it.series_id] || []).push(it);
      } else {
        const d = (it.event_date || "").slice(0, 10);
        singles.push({ ...it, _start: d, _end: d, _ids: [it.id] });
      }
    });
    const seriesRows = [];
    Object.values(seriesMap).forEach((arr) => {
      const sorted = [...arr].sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
      const recEv = sorted.find((e) => e.recurrence);
      const rec = recEv ? recurrenceLabel(recEv) : null;
      const allIds = sorted.map((e) => e.id);
      if (rec) {
        sorted.forEach((occ) => {
          const d = (occ.event_date || "").slice(0, 10);
          seriesRows.push({ ...occ, _start: d, _end: d, _multi: false, _recurring: true, _recurrence: rec, _seriesCount: sorted.length, _ids: allIds });
        });
      } else {
        const first = sorted[0];
        const start = (first.event_date || "").slice(0, 10);
        const end = (sorted[sorted.length - 1].event_date || "").slice(0, 10);
        seriesRows.push({ ...first, _start: start, _end: end, _multi: sorted.length > 1, _ids: allIds });
      }
    });
    const monthStart = `${monthPrefix}-01`;
    const monthEnd = `${monthPrefix}-31`;
    const inMonth = [...seriesRows, ...singles]
      .filter((it) => it._start.slice(0, 7) === monthPrefix || it._end.slice(0, 7) === monthPrefix || (it._start < monthStart && it._end > monthEnd));
    const byStart = (a, b) => (a._start || "").localeCompare(b._start || "") || (a.start_time || "").localeCompare(b.start_time || "");
    // Upcoming/ongoing first (nearest first), then past items (most recent first).
    const upcoming = inMonth.filter((it) => it._end >= todayKey).sort(byStart);
    const past = inMonth.filter((it) => it._end < todayKey).sort((a, b) => byStart(b, a)).map((it) => ({ ...it, _past: true }));
    return [...upcoming, ...past];
  }, [items, monthPrefix, todayKey]);
  useEffect(() => { setMonthPage(1); }, [monthPrefix]);
  // Upcoming events fill the first pages (max 8 each); past events always start on their own page.
  const monthChunks = useMemo(() => {
    const chunk = (arr) => Array.from({ length: Math.ceil(arr.length / MONTH_PAGE) }, (_, i) => arr.slice(i * MONTH_PAGE, (i + 1) * MONTH_PAGE));
    return [...chunk(monthItems.filter((it) => !it._past)), ...chunk(monthItems.filter((it) => it._past))];
  }, [monthItems]);
  const monthPages = Math.max(1, monthChunks.length);
  const curMonthPage = Math.min(monthPage, monthPages);
  const pageItems = monthChunks[curMonthPage - 1] || [];
  const pageFrom = monthChunks.slice(0, curMonthPage - 1).reduce((n, c) => n + c.length, 0) + 1;
  const listRef = useRef(null);
  useScrollPaging(listRef, curMonthPage, monthPages, setMonthPage);
  const statusesOf = (it) => (attendanceFor && it.type === "event"
    ? [...new Set((it._multi ? it._ids : [it.id]).map(attendanceFor).filter((s) => s && s !== "present"))] : []);
  const cardActions = (it) => (
    <>
      <ConflictControls it={it} info={conflictInfo?.(it)} onConflict={onConflict} prefix="month-" compact />
      <VolunteerControls it={it} onVolunteer={onVolunteer} prefix="month-" compact />
      {showAttendance && it.type === "event" && <AttendanceButton onClick={() => onAttendance(it)} testId={`month-attendance-btn-${it.id}`} compact />}
      {canManage && it.type === "event" && it.source !== "external" && (
        <>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(it)} data-testid={`month-edit-event-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => onDelete(it)} data-testid={`month-delete-event-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </>
      )}
    </>
  );

  return (
    <>
      <Card className="p-3 sm:p-4 border-slate-200 shadow-sm select-none" style={{ touchAction: "pan-y" }} onPointerDown={onPointerDown} onPointerUp={onPointerUp} data-testid="schedule-calendar">
        <div className="mb-3 sm:mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800 font-heading"><TapInfo tip="Tap a date with events to see that day's details." testId="calendar-tap-info" />{MONTHS[month]} {year}</h3>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-10 w-10 sm:h-8 sm:w-8" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" className="h-10 sm:h-8" onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setSelected(todayKey); setMonthPage(1); }} data-testid="cal-today">Today</Button>
            <Button size="icon" variant="ghost" className="h-10 w-10 sm:h-8 sm:w-8" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center">
          {DOW.map((d) => <div key={d} className="pb-1.5 sm:pb-2 text-[10px] sm:text-xs font-bold uppercase tracking-wide text-slate-400">{d}</div>)}
          {cells.map((d, i) => {
            if (d === null) return <div key={`e${i}`} />;
            const key = ymd(new Date(year, month, d));
            const dayItems = byDate[key] || [];
            const isSel = key === selected;
            const isToday = key === todayKey;
            const tappable = dayItems.length > 0;
            return (
              <button
                key={key}
                onClick={() => pickDay(key)}
                data-testid={`cal-day-${key}`}
                data-tappable={tappable ? "true" : undefined}
                aria-label={`${prettyDate(key)}${dayItems.length ? `, ${dayItems.length} item${dayItems.length === 1 ? "" : "s"}` : ""}`}
                className={`group relative flex h-12 sm:h-14 lg:h-16 flex-col items-center justify-start rounded-md sm:rounded-lg border p-0.5 sm:p-1 text-[15px] sm:text-sm transition-colors active:scale-95
                  ${isSel ? "border-slate-900 bg-slate-900 text-white" : tappable ? "border-slate-100 hover:border-slate-300 hover:bg-[#e8eff8] active:bg-[#e8eff8]" : "border-slate-100 hover:border-slate-300"}
                  ${isToday && !isSel ? "ring-1 ring-sky-400" : ""}`}
              >
                {tappable && <TapArrow size="h-3 w-3" className={`absolute right-0 top-1/2 -translate-y-1/2 sm:right-0.5 ${isSel ? "!text-white/70" : ""}`} testId={`cal-day-arrow-${key}`} />}
                <span className={`font-semibold tabular-nums ${isSel ? "text-white" : "text-slate-700"}`}>{d}</span>
                {dayItems.length > 0 && (
                  <span className="mt-1 flex items-center gap-0.5" data-testid={`cal-dots-${key}`}>
                    {dayItems.slice(0, 3).map((it, idx) => (
                      <span key={idx} className={`h-2 w-2 sm:h-1.5 sm:w-1.5 rounded-full ${it.type === "conference" ? "bg-blue-400" : it.type === "volunteer" ? "bg-indigo-500" : it.source === "external" ? "bg-amber-500" : isSel ? "bg-emerald-400 ring-1 ring-white" : "bg-emerald-500"}`} />
                    ))}
                    {dayItems.length > 3 && <span className={`text-[9px] font-bold leading-none ${isSel ? "text-white" : "text-slate-500"}`} data-testid={`cal-more-${key}`}>+{dayItems.length - 3}</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Event</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-400" /> Conference</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Imported</span>
          {items.some((i) => i.type === "volunteer") && <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-indigo-500" /> Volunteer</span>}
        </div>
      </Card>

    <DayPreviewSheet open={!!preview} title={preview ? prettyDate(preview) : ""}
      items={preview ? [...(byDate[preview] || [])].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")) : []}
      onClose={() => setPreview(null)} onViewDetails={viewDayDetails} renderActions={sheetActions} tintFor={sheetTint} />
    <Card ref={listRef} className="mt-6 p-4 sm:p-5 border-slate-200 shadow-sm" data-testid="schedule-month-list">
      <h3 className="mb-4 text-base font-semibold text-slate-800 font-heading">{MONTHS[month]} {year} — Events & Conferences</h3>
      {monthItems.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="month-list-empty">Nothing scheduled this month.</p>
      ) : (
        <>
        {conflictInfo && monthItems.some((it) => tintOf(it)) && <ConflictKey />}
        <div className="space-y-3" data-testid="month-list-items">
          {pageItems.map((it) => {
            const tint = tintOf(it);
            return <EventCard key={it.id} it={tint ? { ...it, _conflictStatus: conflictInfo(it).conflict.status } : it} tint={tint}
              hl={flash && it._start <= flash && it._end >= flash} statuses={statusesOf(it)} actions={cardActions(it)} />;
          })}
        </div>
        </>
      )}
      {monthPages > 1 && <p className="mt-3 text-center text-xs text-slate-400" data-testid="month-list-scroll-hint">Keep scrolling past the last event for the next page, or above the first to go back.</p>}
      {monthPages > 1 && <Pagination page={curMonthPage} pageSize={MONTH_PAGE} total={monthItems.length} onPage={setMonthPage} testId="month-list-pagination"
        pages={monthPages} range={[pageFrom, pageFrom + pageItems.length - 1]} />}
    </Card>
    </>
  );
}

export default ScheduleCalendar;
